import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

/**
 * Tracker dédié aux modifications AI d'une proposition de sondage
 * (cf. spec produit Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md).
 *
 * Chaque appel à `editPollFromText` qui réussit logge un événement dans la
 * table `analytics_events` (event_type = "poll_modification_ai"). Sert au
 * dashboard admin pour mesurer le volume et le coût Gemini.
 *
 * Insert direct (table RLS ouverte en INSERT pour permettre les events
 * anonymes) plutôt que via SupportEventLogger pour rester queryable
 * facilement côté admin.
 */

export interface PollModificationEvent {
  initialPromptPreview: string | null;
  modificationRequestPreview: string;
  modificationIndex: number;
  modificationLimit: number;
  pollType: string;
  datesCountBefore: number;
  datesCountAfter: number;
}

export interface PollModificationStats {
  countLast24h: number;
  countLast7d: number;
  countLast30d: number;
}

const EVENT_TYPE = "poll_modification_ai";

export class PollModificationTracker {
  /**
   * Persiste un événement de modification AI. Best-effort : log local
   * uniquement si l'insert Supabase échoue, jamais d'exception remontée
   * à l'appelant pour ne pas bloquer le flow UI.
   */
  static async track(event: PollModificationEvent): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { error } = await supabase.from("analytics_events").insert({
        event_type: EVENT_TYPE,
        event_data: event,
        user_id: user?.id ?? null,
      });

      if (error) {
        // Best-effort : si l'insert est refusé (RLS, auth manquante, etc.)
        // on dégrade silencieusement plutôt que polluer la console des
        // utilisateurs finaux. L'admin verra simplement un compteur plus
        // bas — le flow de modification AI lui-même n'est pas impacté.
        logger.debug(
          "PollModificationTracker.track skipped (insert refused)",
          "analytics",
          { code: error.code, message: error.message },
        );
      }
    } catch (err) {
      logger.debug(
        "PollModificationTracker.track skipped (exception)",
        "analytics",
        { error: err instanceof Error ? err.message : String(err) },
      );
    }
  }

  /**
   * Récupère le compteur de modifications AI sur 24h / 7j / 30j. Utilisé
   * par le widget admin. Best-effort : retourne 0 si la query échoue.
   */
  static async getStats(): Promise<PollModificationStats> {
    const now = Date.now();
    const d24h = new Date(now - 24 * 60 * 60 * 1000).toISOString();
    const d7d = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
    const d30d = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();

    const safeCount = async (since: string): Promise<number> => {
      const { count, error } = await supabase
        .from("analytics_events")
        .select("*", { count: "exact", head: true })
        .eq("event_type", EVENT_TYPE)
        .gte("created_at", since);
      if (error) {
        logger.warn(
          "PollModificationTracker.getStats: count query failed",
          "analytics",
          { error: error.message, since },
        );
        return 0;
      }
      return count ?? 0;
    };

    const [countLast24h, countLast7d, countLast30d] = await Promise.all([
      safeCount(d24h),
      safeCount(d7d),
      safeCount(d30d),
    ]);

    return { countLast24h, countLast7d, countLast30d };
  }
}
